import {clsx} from 'clsx'
import {useEffect, useRef, useState} from 'react'
import styles from './ActivityTicker.module.css'

const ANIMATION_DURATION_MS = 300
const DEFAULT_MIN_DISPLAY_MS = 1000
const TRANSITION_STYLE = {willChange: 'transform, opacity'} as const

type TickerItem = {
  key: number
  text: string
}

type ActivityTickerProps = {
  text: string
  className?: string
  minDisplayMs?: number
}

export function ActivityTicker({text, className, minDisplayMs = DEFAULT_MIN_DISPLAY_MS}: ActivityTickerProps) {
  const [mountedAt] = useState(() => Date.now())
  const nextKeyRef = useRef(1)
  const displayedTextRef = useRef(text)
  const displayedAtRef = useRef(mountedAt)
  const animationUnlockAtRef = useRef(0)
  const pendingTextRef = useRef<string | null>(null)
  const pendingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const pruneTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const [items, setItems] = useState<TickerItem[]>([{key: 0, text}])

  useEffect(() => {
    const clearPendingTimer = () => {
      if (pendingTimerRef.current !== null) {
        clearTimeout(pendingTimerRef.current)
        pendingTimerRef.current = null
      }
    }

    const startTransition = (nextText: string) => {
      const transitionStart = Date.now()
      const nextItem = {key: nextKeyRef.current++, text: nextText}

      displayedTextRef.current = nextText
      displayedAtRef.current = transitionStart
      animationUnlockAtRef.current = transitionStart + ANIMATION_DURATION_MS
      setItems(currentItems => {
        const currentItem = currentItems.at(-1)
        return currentItem ? [currentItem, nextItem] : [nextItem]
      })

      if (pruneTimerRef.current !== null) {
        clearTimeout(pruneTimerRef.current)
      }
      pruneTimerRef.current = setTimeout(() => {
        setItems(currentItems => {
          const currentItem = currentItems.at(-1)
          return currentItem ? [currentItem] : currentItems
        })
        pruneTimerRef.current = null
      }, ANIMATION_DURATION_MS)
    }

    if (text === displayedTextRef.current) {
      pendingTextRef.current = null
      clearPendingTimer()
      return
    }

    pendingTextRef.current = text
    clearPendingTimer()

    const now = Date.now()
    const minDisplayRemaining = Math.max(0, displayedAtRef.current + minDisplayMs - now)
    const animationRemaining = Math.max(0, animationUnlockAtRef.current - now)
    const delay = Math.max(minDisplayRemaining, animationRemaining)

    if (delay === 0) {
      pendingTextRef.current = null
      startTransition(text)
      return
    }

    pendingTimerRef.current = setTimeout(() => {
      const pendingText = pendingTextRef.current
      pendingTextRef.current = null
      pendingTimerRef.current = null

      if (pendingText !== null && pendingText !== displayedTextRef.current) {
        startTransition(pendingText)
      }
    }, delay)
  }, [minDisplayMs, text])

  useEffect(
    () => () => {
      if (pendingTimerRef.current !== null) {
        clearTimeout(pendingTimerRef.current)
      }
      if (pruneTimerRef.current !== null) {
        clearTimeout(pruneTimerRef.current)
      }
    },
    [],
  )

  return (
    <span className={clsx(styles.container, className)} aria-hidden="true">
      {items.map((item, index) => (
        <span
          key={item.key}
          aria-hidden="true"
          style={items.length > 1 ? TRANSITION_STYLE : undefined}
          className={clsx(
            styles.item,
            item.key === 0 && items.length === 1 && styles.initial,
            items.length > 1 && index === 0 && styles.exiting,
            items.length > 1 && index === items.length - 1 && styles.entering,
          )}
        >
          {item.text}
        </span>
      ))}
    </span>
  )
}
